import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { OPTIONS } from '../const/globals.const';
import { HospitalDal } from '../dal/hospital.dal';
import { Hospital } from '../models/hospital.model';
import { getFirstHospitalSelectedData, getSelectedHospitalsData } from '../utils/cross-functions';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class HospitalService {
  constructor(
    private readonly hospitalDal: HospitalDal,
    private readonly userService: UserService
  ) {}

  checkIfHospitalHaveOption(optionName: string): boolean {
    const currentUser = this.userService.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    const hospital = getFirstHospitalSelectedData(currentUser, this.userService.getSelectedHospitals());
    if (hospital?.options) {
      const found = hospital.options.find((f: any) => {
        return f.name === optionName;
      });

      return !!found;
    }

    return false;
  }

  checkIfHospitalHaveOptionsButton(optionName: string): boolean {
    const currentUser = this.userService.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    const hospitals = getSelectedHospitalsData(currentUser, this.userService.getSelectedHospitals());

    for (const hospital of hospitals) {
      if (hospital?.options) {
        const option = hospital.options.find((e: any) => e.name === optionName);

        if (option) {
          const value = option.canDataBeRequestByUsers;

          if (value === false || value === 'false') {
            return false;
          }

          if (value === true || value === 'true') {
            return true;
          }

          return true;
        }
      }
    }

    return true;
  }

  checkIfHospitalAllowsShowingRoles(): boolean {
    const currentUser = this.userService.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    const hospital = getFirstHospitalSelectedData(currentUser, this.userService.getSelectedHospitals());

    return hospital?.showRoles ?? true;
  }

  checkIfHospitalAllowsProgression(): boolean {
    const currentUser = this.userService.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    const hospital = getFirstHospitalSelectedData(currentUser, this.userService.getSelectedHospitals());

    return hospital?.settings?.isProgressionEnabled ?? true;
  }

  doesHospitalHaveProgramsDataPipelineOption(): boolean {
    return this.checkIfHospitalHaveOption(OPTIONS.PROGRAMS_DATA_PIPELINE);
  }

  doesHospitalHaveVacationsDataPipelineOption(): boolean {
    return this.checkIfHospitalHaveOption(OPTIONS.VACATIONS_DATA_PIPELINE);
  }

  doesHospitalHaveCalendarsDataPipelineOption(): boolean {
    return this.checkIfHospitalHaveOption(OPTIONS.CALENDARS_DATA_PIPELINE);
  }

  //new pour gérer l'etat de disabled des boutons de connecteurs dans pilotage RH et vacations par salle et interventions par salle et statop

  doesHospitalHaveProgramsDataPipelineButton(): boolean {
    return this.checkIfHospitalHaveOptionsButton(OPTIONS.PROGRAMS_DATA_PIPELINE);
  }

  doesHospitalHaveVacationsDataPipelineButton(): boolean {
    return this.checkIfHospitalHaveOptionsButton(OPTIONS.VACATIONS_DATA_PIPELINE);
  }

  doesHospitalHaveCalendarsDataPipelineButton(): boolean {
    return this.checkIfHospitalHaveOptionsButton(OPTIONS.CALENDARS_DATA_PIPELINE);
  }

  doesHospitalHaveStatsCustomizationOption(): boolean {
    return this.checkIfHospitalHaveOption(OPTIONS.STATS_CUSTOMIZATION);
  }

  doesHospitalHaveIadesNeedsMorningAfternoonOption(): boolean {
    return this.checkIfHospitalHaveOption(OPTIONS.IADES_NEEDS_MORNING_AFTERNOON);
  }

  doesHospitalHaveBlockedPlanning(): boolean {
    const currentUser = this.userService.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    const hospital = getFirstHospitalSelectedData(currentUser, this.userService.getSelectedHospitals());
    if (hospital) {
      return hospital.isBlocked;
    }

    return false;
  }

  modifyHospital(hospital: Hospital): Observable<Hospital> {
    return this.hospitalDal.modifyHospital(hospital);
  }

  getHospital(hospitalId?: string): Observable<any> {
    return this.hospitalDal.getHospital(hospitalId);
  }

  getHospitalsByHospitalName(hospitalName: string): Observable<any> {
    return this.hospitalDal.getHospitalsByHospitalName(hospitalName);
  }

  editIsBlockedOneHospital(hospitalId: string, isBlocked: boolean): Observable<any> {
    return this.hospitalDal.editIsBlockedOneHospital(hospitalId, isBlocked);
  }

  updateNursesAutofillingSettings(args: { hospitalId: string; nursesAutofillingSettings: any }): Observable<any> {
    return this.hospitalDal.updateNursesAutofillingSettings(args);
  }

  updateAutofillingSettings(args: { hospitalId: string; autofillingSettings: any }): Observable<any> {
    return this.hospitalDal.updateAutofillingSettings(args);
  }

  updateAnesthsAutofillingSettings(args: { hospitalId: string; anesthsAutofillingSettings: any }): Observable<any> {
    return this.hospitalDal.updateAnesthsAutofillingSettings(args);
  }

  updateIadesAutofillingSettings(args: { hospitalId: string; iadesAutofillingSettings: any }): Observable<any> {
    return this.hospitalDal.updateIadesAutofillingSettings(args);
  }

  checkCompatibility(): Observable<any> {
    return this.hospitalDal.checkCompatibility();
  }

  editCycleHospital(hospitalId: string, data: object): Observable<any> {
    return this.hospitalDal.editCycleHospital(hospitalId, data);
  }

  getHospitalIndicators(): Observable<any> {
    return this.hospitalDal.getHospitalIndicators();
  }

  updateHospitalInfos(args: { hospitalId: string; infos: any }): Observable<Hospital> {
    return this.hospitalDal.updateHospitalInfos(args.hospitalId, args.infos);
  }
}
